// src/auth/error-codes.ts
var AuthErrorCode = {
  USER_ARCHIVED: "USER_ARCHIVED",
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  INVALID_REFRESH_TOKEN: "INVALID_REFRESH_TOKEN",
  SESSION_REVOKED: "SESSION_REVOKED"
};

// src/user/user.ts
import * as zod from "zod";
var z2 = zod.z ?? zod;
var UserRoleSchema = z2.enum(["WORKER", "MANAGER", "DIRECTOR", "SUPERUSER"]);
var Role = UserRoleSchema.enum;
var UserRolesSchema = z2.array(UserRoleSchema).nonempty("Mindestens eine Rolle ausw\xE4hlen");
var ROLE_RANK = {
  WORKER: 0,
  MANAGER: 1,
  DIRECTOR: 2,
  SUPERUSER: 3
};
var hasRole = (roles, r) => roles.includes(r);
var hasAnyRole = (roles, rs) => roles.some((r) => rs.includes(r));
var hasOnlyRole = (roles, r) => roles.length === 1 && roles[0] === r;
var highestRole = (roles) => roles.reduce((acc, r) => ROLE_RANK[r] > ROLE_RANK[acc] ? r : acc, "WORKER");
var TechnicianProfileDataSchema = z2.object({
  workingDays: z2.array(z2.number().int().min(1).max(7)),
  shiftStart: z2.string(),
  shiftEnd: z2.string(),
  breakStart: z2.string(),
  trades: z2.array(z2.number().int().nonnegative())
});
var CreateUserDtoSchema = z2.object({
  email: z2.string().email(),
  name: z2.string(),
  roles: UserRolesSchema,
  technicianProfile: TechnicianProfileDataSchema.optional(),
  password: z2.string().min(8).optional()
}).superRefine((d, ctx) => {
  if (d.roles.includes("WORKER") && !d.technicianProfile) {
    ctx.addIssue({ code: z2.ZodIssueCode.custom, message: "Techniker-Profil ist erforderlich f\xFCr Handwerker", path: ["technicianProfile"] });
  }
  if (!d.roles.includes("WORKER") && d.technicianProfile) {
    ctx.addIssue({ code: z2.ZodIssueCode.custom, message: "Techniker-Profil nur f\xFCr Handwerker erlaubt", path: ["technicianProfile"] });
  }
});
var UpdateUserDtoSchema = z2.object({
  email: z2.string().email().optional(),
  name: z2.string().optional(),
  roles: UserRolesSchema.optional(),
  technicianProfile: TechnicianProfileDataSchema.optional()
});
var UserStatusFilterSchema = z2.enum(["active", "archived", "all"]);
var UserFiltersSchema = z2.preprocess(
  (raw) => {
    if (raw !== null && typeof raw === "object" && !Array.isArray(raw)) {
      const { role, roles, ...rest } = raw;
      const normalizedRoles = roles !== void 0 ? Array.isArray(roles) ? roles : [roles] : role !== void 0 ? [role] : void 0;
      return normalizedRoles !== void 0 ? { ...rest, roles: normalizedRoles } : rest;
    }
    return raw;
  },
  z2.object({
    page: z2.coerce.number().int().positive().optional(),
    limit: z2.coerce.number().int().positive().max(500).optional(),
    search: z2.string().optional(),
    roles: z2.array(UserRoleSchema).optional(),
    status: UserStatusFilterSchema.optional(),
    tradeId: z2.coerce.number().int().nonnegative().optional()
  })
);

// src/audit-log/audit-log.ts
import * as zod2 from "zod";
var z4 = zod2.z ?? zod2;
var AuditLogEntityTypeSchema = z4.enum(["WORK_ORDER", "APPOINTMENT", "TASK"]);
var AuditLogActionSchema = z4.enum([
  "WORK_ORDER_CREATED",
  "WORK_ORDER_STATUS_CHANGED",
  "WORK_ORDER_COMPLETED",
  "APPOINTMENT_CREATED",
  "APPOINTMENT_UPDATED",
  "APPOINTMENT_RESCHEDULED",
  "APPOINTMENT_TECHNICIAN_CHANGED",
  "APPOINTMENT_TRAVEL_STARTED",
  "APPOINTMENT_STARTED",
  "APPOINTMENT_COMPLETED",
  "APPOINTMENT_DEPARTURE_COMPLETED",
  "APPOINTMENT_TIMES_CORRECTED",
  "APPOINTMENT_PHASE_REVERTED",
  "APPOINTMENT_FOLLOW_UP_REQUESTED",
  "APPOINTMENT_EXPIRED",
  "APPOINTMENT_DELETED",
  "APPOINTMENT_SERIES_UPDATED",
  "APPOINTMENT_SERIES_EXTENDED",
  "APPOINTMENT_SERIES_SHORTENED",
  "APPOINTMENT_DETACHED_FROM_SERIES",
  "TASK_TRAVEL_STARTED",
  "TASK_STARTED",
  "TASK_COMPLETED",
  "TASK_DEPARTURE_COMPLETED",
  "TASK_TIMES_CORRECTED",
  "TASK_PHASE_REVERTED",
  "TASK_RESCHEDULED",
  "TASK_UPDATED",
  "TASK_DELETED",
  "TASK_SERIES_EXTENDED",
  "TASK_SERIES_SHORTENED",
  "TASK_DETACHED_FROM_SERIES"
]);
var AuditLogActorSchema = z4.object({
  id: z4.string(),
  name: z4.string(),
  roles: z4.array(UserRoleSchema)
}).nullable();
var AuditLogSchema = z4.object({
  id: z4.number().int(),
  entityType: AuditLogEntityTypeSchema,
  entityId: z4.string(),
  workOrderId: z4.string().nullable(),
  action: z4.string(),
  description: z4.string(),
  payload: z4.unknown().nullable().optional(),
  actorUserId: z4.number().int().nullable(),
  actorRoles: z4.array(UserRoleSchema),
  actor: AuditLogActorSchema,
  createdAt: z4.string()
});
var AuditLogFiltersSchema = z4.object({
  workOrderId: z4.string()
});

// src/work-order/work-order.ts
import * as zod3 from "zod";
var z6 = zod3.z ?? zod3;
var WorkOrderMieterSchema = z6.object({
  name: z6.string().nullable(),
  telefon: z6.string().nullable(),
  email: z6.string().nullable()
});
var ImmotionAttachmentSchema = z6.object({
  filename: z6.string(),
  mime: z6.string(),
  base64: z6.string()
});
var WorkOrderExternalSnapshotSchema = z6.object({
  auftragJahr: z6.number().int().positive(),
  auftragNr: z6.number().int().positive(),
  auftragsdatum: z6.string().nullable(),
  ausfuehrungBis: z6.string().nullable(),
  fertigmeldungsdat: z6.string().nullable(),
  auftStatus: z6.number().int().nullable(),
  ve: z6.number().int().nullable(),
  wohnung: z6.number().int().nullable(),
  veAdresse: z6.string().nullable(),
  hauseingang: z6.string().nullable(),
  gebaeude: z6.string().nullable(),
  mieter: WorkOrderMieterSchema,
  handwerkerName: z6.string().nullable(),
  gewerkId: z6.number().int().nullable(),
  gewerkeText: z6.string().nullable(),
  leistungText: z6.string().nullable(),
  infoText: z6.string().nullable(),
  individuellerText: z6.string().nullable(),
  erfasserName: z6.string().nullable(),
  isWohnungsmodernisierung: z6.boolean(),
  immotionAttachments: z6.array(ImmotionAttachmentSchema).default([]),
  immotionAttachmentsCount: z6.number().int().nonnegative().default(0)
});
var WorkOrderLocalDataSchema = z6.object({
  updatedAt: z6.string().nullable()
});
var WorkOrderStatusSchema = z6.enum([
  "NOT_PLANNED",
  "PLANNED",
  "FOLLOW_UP_REQUIRED_1",
  "FOLLOW_UP_PLANNED_1",
  "FOLLOW_UP_REQUIRED_2",
  "FOLLOW_UP_PLANNED_2",
  "COMPLETED",
  "COMPLETED_FOLLOW_UP_1",
  "COMPLETED_FOLLOW_UP_2"
]);
var WorkOrderSchema = z6.object({
  workOrderId: z6.string().nullable(),
  status: WorkOrderStatusSchema,
  external: WorkOrderExternalSnapshotSchema
});
var WorkOrderSortFieldSchema = z6.enum([
  "workOrderId",
  "mieter",
  "handwerker",
  "datum",
  "ve",
  "hauseingang",
  "wohnung"
]);
var WorkOrderSortDirectionSchema = z6.enum(["ASC", "DESC"]);
var WorkOrderFiltersSchema = z6.object({
  page: z6.coerce.number().int().positive().optional(),
  limit: z6.coerce.number().int().positive().max(500).optional(),
  workOrderId: z6.string().optional(),
  mieter: z6.string().optional(),
  handwerker: z6.string().optional(),
  datum: z6.string().optional(),
  datumFrom: z6.string().optional(),
  datumTo: z6.string().optional(),
  wohnung: z6.coerce.number().int().optional(),
  hauseingang: z6.string().optional(),
  ve: z6.coerce.number().int().optional(),
  veAdresse: z6.string().optional(),
  sortField: WorkOrderSortFieldSchema.optional(),
  sortDirection: WorkOrderSortDirectionSchema.optional(),
  local: z6.preprocess((v) => v === "false" ? false : v === "true" ? true : v, z6.boolean().optional()),
  status: WorkOrderStatusSchema.optional(),
  gewerkId: z6.coerce.number().int().optional()
});
var CreateWorkOrderDtoSchema = z6.object({
  workOrderId: z6.string()
});

// src/work-order/work-order-detail.ts
import { z as z12 } from "zod";

// src/appointment/appointment.ts
import { z as z11 } from "zod";

// src/schedule/schedule-entry.ts
import { z as z8 } from "zod";

// src/absence/absence.ts
import { z as z7 } from "zod";
var AbsenceCategorySchema = z7.enum([
  "SICK_LEAVE",
  "VACATION",
  "TRAINING",
  "PERSONAL_LEAVE"
]);
var AbsenceTechnicianSchema = z7.object({
  id: z7.number().int(),
  fullName: z7.string()
});
var AbsenceSchema = z7.object({
  id: z7.number().int(),
  createdByUserId: z7.number().int(),
  /** NOT NULL — with `entryType` gone, every absence has a category. */
  category: AbsenceCategorySchema,
  comment: z7.string().nullable().optional(),
  /** Author intent. Per-technician occupancy lives on `entries`. */
  startsAt: z7.string().datetime(),
  endsAt: z7.string().datetime(),
  technicians: z7.array(AbsenceTechnicianSchema),
  createdAt: z7.string().datetime()
});
var CreateAbsenceDtoSchema = z7.object({
  technicianIds: z7.array(z7.coerce.number().int().positive()).min(1),
  category: AbsenceCategorySchema,
  comment: z7.string().nullable().optional(),
  startsAt: z7.string().datetime(),
  endsAt: z7.string().datetime(),
  /**
   * Book anyway and flag every displaced appointment/task `needsRescheduling`. An absence never
   * yields to another absence it overlaps — force books both, flags neither.
   */
  force: z7.boolean().optional()
});
var UpdateAbsenceDtoSchema = z7.object({
  technicianIds: z7.array(z7.coerce.number().int().positive()).min(1).optional(),
  category: AbsenceCategorySchema.optional(),
  comment: z7.string().nullable().optional(),
  startsAt: z7.string().datetime().optional(),
  endsAt: z7.string().datetime().optional(),
  force: z7.boolean().optional()
});
var AbsenceSortFieldSchema = z7.enum(["category", "startsAt", "endsAt"]);
var AbsenceSortDirectionSchema = z7.enum(["ASC", "DESC"]);
var AbsenceFiltersSchema = z7.object({
  page: z7.coerce.number().int().positive().optional(),
  limit: z7.coerce.number().int().positive().max(500).optional(),
  technicianName: z7.string().optional(),
  category: AbsenceCategorySchema.optional(),
  dateFrom: z7.string().optional(),
  dateTo: z7.string().optional(),
  sortField: AbsenceSortFieldSchema.optional(),
  sortDirection: AbsenceSortDirectionSchema.optional()
});
var AbsenceListResultSchema = z7.object({
  items: z7.array(AbsenceSchema),
  meta: z7.object({
    page: z7.number().int(),
    limit: z7.number().int(),
    total: z7.number().int()
  })
});

// src/schedule/schedule-entry.ts
var ScheduleEntryTypeSchema = z8.enum([
  "APPOINTMENT",
  "TASK",
  "ABSENCE",
  "BREAK"
]);
var RescheduleCauseSchema = z8.enum([
  /** Displaced by an absence, or by any block force-planned on top of it. */
  "ABSENCE",
  /** Force-planned onto a day the technician does not work ("Trotzdem planen"). */
  "OUTSIDE_WORKING_HOURS",
  /** Flagged wholesale when the technician was archived. */
  "TECHNICIAN_ARCHIVED"
]);
var SegmentedSlotSchema = z8.object({
  kind: z8.literal("SEGMENTED"),
  /** Work start. The block starts `travelMinutes` earlier. */
  workStartsAt: z8.string().datetime(),
  travelMinutes: z8.number().int().min(0).default(0),
  workMinutes: z8.number().int().min(0),
  departureMinutes: z8.number().int().min(0).default(0)
});
var SpanSlotSchema = z8.object({
  kind: z8.literal("SPAN"),
  startsAt: z8.string().datetime(),
  endsAt: z8.string().datetime()
});
var SlotSchema = z8.discriminatedUnion("kind", [SegmentedSlotSchema, SpanSlotSchema]).superRefine((slot, ctx) => {
  if (slot.kind === "SEGMENTED") {
    if (slot.travelMinutes + slot.workMinutes + slot.departureMinutes <= 0) {
      ctx.addIssue({
        code: z8.ZodIssueCode.custom,
        message: "Slot must occupy a positive amount of time",
        path: ["workMinutes"]
      });
    }
    return;
  }
  if (new Date(slot.endsAt).getTime() <= new Date(slot.startsAt).getTime()) {
    ctx.addIssue({
      code: z8.ZodIssueCode.custom,
      message: "endsAt must be after startsAt",
      path: ["endsAt"]
    });
  }
});
var ScheduleEntryTechnicianSchema = z8.object({
  id: z8.number().int(),
  fullName: z8.string().nullable(),
  avatar: z8.string().nullable().optional()
});
var ScheduleEntrySchema = z8.object({
  /** `null` marks a default break projected from the technician profile — it has no row. */
  id: z8.number().int().nullable(),
  type: ScheduleEntryTypeSchema,
  technicianId: z8.number().int(),
  createdByUserId: z8.number().int().nullable(),
  /** Derived occupancy, travel included. CHECK-enforced, never client-supplied. */
  startsAt: z8.string().datetime(),
  endsAt: z8.string().datetime(),
  plannedWorkStartsAt: z8.string().datetime(),
  plannedTravelMinutes: z8.number().int().min(0),
  plannedWorkMinutes: z8.number().int().min(0),
  plannedDepartureMinutes: z8.number().int().min(0),
  actualWorkStartsAt: z8.string().datetime().nullable(),
  /**
   * Technician-reported end (from an "HH:mm" input). NOT the occupancy end — `endsAt` is
   * computed from durations. These may legitimately diverge; do not "fix" it.
   */
  actualEndsAt: z8.string().datetime().nullable(),
  /**
   * Raw phase events stamped by "Anfahrt starten" / "Abfahrt beenden" — unrounded evidence.
   * The billable values stay the accepted minutes below; the runtime phase derives from these
   * via `deriveWorkPhase` (work-item). Always null for ABSENCE/BREAK entries.
   */
  travelStartedAt: z8.string().datetime().nullable(),
  departureEndedAt: z8.string().datetime().nullable(),
  actualTravelMinutes: z8.number().int().min(0).nullable(),
  actualWorkMinutes: z8.number().int().min(0).nullable(),
  actualDepartureMinutes: z8.number().int().min(0).nullable(),
  allDay: z8.boolean(),
  needsRescheduling: z8.boolean(),
  reschedulingCause: RescheduleCauseSchema.nullable(),
  /** Only meaningful for cause `ABSENCE` — the category of the absence that displaced this block. */
  reschedulingReason: AbsenceCategorySchema.nullable(),
  reschedulingRequestedAt: z8.string().datetime().nullable(),
  appointmentId: z8.number().int().nullable(),
  taskId: z8.number().int().nullable(),
  absenceId: z8.number().int().nullable(),
  technician: ScheduleEntryTechnicianSchema.nullable().optional(),
  /** Human-readable owner label for calendar rendering. */
  label: z8.string().nullable().optional(),
  /**
   * Present on ABSENCE entries: the parent's category, denormalised so a calendar can colour
   * and label the block without a second request for the absence itself.
   */
  category: AbsenceCategorySchema.nullable().optional(),
  createdAt: z8.string().datetime().nullable(),
  updatedAt: z8.string().datetime().nullable()
});
var ScheduleConflictItemSchema = z8.object({
  /** `null` for a default break projected from the technician profile. */
  entryId: z8.number().int().nullable(),
  type: ScheduleEntryTypeSchema,
  technicianId: z8.number().int(),
  technicianName: z8.string(),
  startsAt: z8.string().datetime(),
  endsAt: z8.string().datetime(),
  label: z8.string(),
  appointmentId: z8.number().int().nullable().optional(),
  taskId: z8.number().int().nullable().optional(),
  absenceId: z8.number().int().nullable().optional(),
  /** Only set for type ABSENCE — the category that wins the overlap and never yields it. */
  absenceCategory: AbsenceCategorySchema.nullable().optional()
});
var ScheduleWorkingHoursViolationReasonSchema = z8.enum([
  /** The block touches a calendar day absent from `TechnicianProfile.workingDays`. */
  "NON_WORKING_DAY",
  /** The block falls outside `[shiftStart, shiftEnd)` for its day. */
  "OUTSIDE_SHIFT"
]);
var ScheduleWorkingHoursViolationSchema = z8.object({
  reason: ScheduleWorkingHoursViolationReasonSchema,
  technicianId: z8.number().int(),
  technicianName: z8.string(),
  /** The offending LOCAL calendar day as `YYYY-MM-DD` in Europe/Berlin. Never an instant. */
  date: z8.string(),
  /** The planned block that was rejected, so the client can render what it asked for. */
  startsAt: z8.string().datetime(),
  endsAt: z8.string().datetime(),
  /** The shift window for `date`. Null for NON_WORKING_DAY — there is no shift that day. */
  shiftStartsAt: z8.string().datetime().nullable(),
  shiftEndsAt: z8.string().datetime().nullable()
});

// src/schedule/schedule-series.ts
import { z as z9 } from "zod";
var RecurrenceFrequencySchema = z9.enum(["WEEKLY", "BIWEEKLY", "MONTHLY"]);
var ScheduleSeriesTypeSchema = z9.enum(["APPOINTMENT", "TASK"]);
var RecurrenceInputSchema = z9.object({
  frequency: RecurrenceFrequencySchema,
  until: z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format YYYY-MM-DD")
});
var ScheduleSeriesSchema = z9.object({
  id: z9.string().uuid(),
  type: ScheduleSeriesTypeSchema,
  frequency: RecurrenceFrequencySchema,
  /**
   * Work start of the first occurrence, and the anchor every later one is derived from. For
   * MONTHLY this is what carries the original day-of-month, so a series starting on the 31st
   * yields 28 Feb and then 31 Mar rather than drifting to 28 Mar.
   */
  startsAt: z9.string().datetime(),
  /** Horizon — occurrences exist up to and including the end of this local day. */
  endsAt: z9.string().datetime(),
  createdByUserId: z9.number().int(),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var UpdateScheduleSeriesDtoSchema = z9.object({
  until: z9.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format YYYY-MM-DD"),
  /**
   * Generate occurrences even on days the technician does not work. Extending is all-or-nothing,
   * so a MONTHLY series growing into a Saturday is otherwise rejected in full.
   */
  allowOutsideWorkingHours: z9.boolean().optional()
});

// src/work-item/work-item.ts
import { z as z10 } from "zod";
var WorkPhaseSchema = z10.enum(["ANFAHRT", "ARBEIT", "ABFAHRT", "DONE"]);
function deriveWorkPhase(entry) {
  if (entry.actualEndsAt != null) {
    if (entry.plannedDepartureMinutes > 0 && entry.departureEndedAt == null) {
      return "ABFAHRT";
    }
    return "DONE";
  }
  if (entry.actualWorkStartsAt != null) return "ARBEIT";
  if (entry.travelStartedAt != null) return "ANFAHRT";
  return null;
}
var isSessionOpen = (phase) => phase === "ANFAHRT" || phase === "ARBEIT" || phase === "ABFAHRT";
var CompleteWorkItemDtoSchema = z10.object({
  travelStartedAt: z10.string().datetime().optional(),
  actualWorkStartsAt: z10.string().datetime().optional(),
  actualEndsAt: z10.string().datetime().optional()
});
var TechnicianAttachmentSchema = z10.object({
  id: z10.number().int(),
  /** `/static/<uuid>.<ext>` — same static mount as the office attachments. */
  url: z10.string(),
  /** The name the file had on the phone; the stored name is a UUID. */
  originalName: z10.string(),
  mimeType: z10.string().nullable(),
  sizeBytes: z10.number().int().nullable(),
  uploadedAt: z10.string().datetime(),
  uploadedBy: z10.object({
    id: z10.number().int(),
    fullName: z10.string()
  })
});
var UpdateTechnicianCommentDtoSchema = z10.object({
  technicianComment: z10.string().max(4e3).nullable()
});
var EndDepartureDtoSchema = z10.object({
  departureEndedAt: z10.string().datetime().optional()
});
var WorkTimeFieldSchema = z10.enum([
  "travelStartedAt",
  "actualWorkStartsAt",
  "actualEndsAt",
  "departureEndedAt"
]);
var WorkSessionItemTypeSchema = z10.enum(["APPOINTMENT", "TASK"]);
var CurrentWorkSessionSchema = z10.object({
  itemType: WorkSessionItemTypeSchema,
  /** Appointment id or task id, depending on `itemType`. */
  itemId: z10.number().int(),
  /** Work-order id for appointments, `null` for tasks. */
  workOrderId: z10.string().nullable(),
  /** Owner label for the banner (trade name / task title). */
  title: z10.string(),
  phase: WorkPhaseSchema,
  /** When the CURRENT phase began — the banner's timer counts from here. */
  phaseStartedAt: z10.string().datetime(),
  /** Planned minutes for the current phase; the timer turns amber beyond it. */
  plannedPhaseMinutes: z10.number().int().min(0)
});
var CurrentWorkSessionResponseSchema = z10.object({
  session: CurrentWorkSessionSchema.nullable()
});

// src/appointment/appointment.ts
var AppointmentStatusSchema = z11.enum([
  "PLANNED",
  "FOLLOW_UP",
  "COMPLETED",
  "COMPLETED_WITH_FOLLOW_UP",
  "EXPIRED"
]);
var AppointmentTechnicianSchema = z11.object({
  id: z11.number().int(),
  name: z11.string().nullable(),
  avatar: z11.string().nullable()
});
var AppointmentSchema = z11.object({
  id: z11.number().int(),
  workOrderId: z11.string(),
  tradeCode: z11.number().int(),
  sequenceNumber: z11.number().int().min(1),
  attachments: z11.array(z11.string()).nullable(),
  status: AppointmentStatusSchema,
  followUpReason: z11.string().nullable().optional(),
  description: z11.string().nullable().optional(),
  technicianComment: z11.string().nullable().optional(),
  /**
   * Files the technician sent from the field — see `TechnicianAttachmentSchema` for why these are
   * not merged into `attachments`. Always present, empty when nothing was sent.
   */
  technicianAttachments: z11.array(TechnicianAttachmentSchema),
  seriesId: z11.string().uuid().nullable().optional(),
  createdAt: z11.string(),
  updatedAt: z11.string(),
  schedule: ScheduleEntrySchema,
  technician: AppointmentTechnicianSchema.nullable(),
  workOrder: WorkOrderSchema.nullable()
});
var CreateAppointmentDtoSchema = z11.object({
  workOrderId: z11.string(),
  assignedTechnicianId: z11.coerce.number().int().positive(),
  plannedStart: z11.string().datetime(),
  tradeCode: z11.number().int().min(0),
  plannedDepartureDuration: z11.number().int().min(0),
  plannedTravelDuration: z11.number().int().min(0),
  plannedWorkDuration: z11.number().int().positive(),
  attachments: z11.array(z11.string()).nullable(),
  description: z11.string().nullable().optional(),
  isFullDay: z11.boolean().optional(),
  recurrence: RecurrenceInputSchema.optional(),
  /**
   * Book anyway and flag every displaced entry `needsRescheduling`. Never bypasses completed
   * work — `SCHEDULE_COMPLETED_BLOCKERS` is thrown regardless.
   */
  force: z11.boolean().optional(),
  /**
   * Plan this even though the technician does not work then — a non-working day, or outside
   * their shift. Distinct from `force`: that one overrides an *overlap* and writes (it flags
   * every entry it displaced), this one overrides a read-only rule about the technician and
   * displaces nothing. A slot can need both, either, or neither.
   *
   * With `recurrence` it applies to every occurrence: creation is all-or-nothing, so a MONTHLY
   * series that eventually lands on a Saturday needs this or the whole series is rejected.
   */
  allowOutsideWorkingHours: z11.boolean().optional()
});
var UpdateAppointmentDtoSchema = z11.object({
  assignedTechnicianId: z11.coerce.number().int().positive(),
  plannedStart: z11.string().datetime(),
  tradeCode: z11.number().int().min(0),
  plannedDepartureDuration: z11.number().int().min(0),
  plannedTravelDuration: z11.number().int().min(0),
  plannedWorkDuration: z11.number().int().positive(),
  attachments: z11.array(z11.string()).nullable(),
  followUpReason: z11.string().nullable(),
  actualStart: z11.string().datetime(),
  actualEnd: z11.string().datetime(),
  actualWorkDuration: z11.number().int().min(0),
  actualTravelDuration: z11.number().int().min(0),
  actualDepartureDuration: z11.number().int().min(0),
  description: z11.string().nullable(),
  isFullDay: z11.boolean(),
  /**
   * A manager's manual correction of the lifecycle state.
   *
   * The technician-driven path is `PATCH :id/complete` and `:id/follow-up`, which also record the
   * actuals and demand that the appointment was started. This one only moves the status — but it
   * carries the same WorkOrder side effect, so the Auftrag cannot drift out of sync with the work
   * that was actually signed off.
   *
   * There is no transition table: any status may follow any other. `EXPIRED` is normally the
   * nightly cron's to write, and setting `PLANNED` on an appointment whose start is in the past
   * will be expired again the following night — reviving an old appointment means moving its date
   * too.
   *
   * Never spread by `forFullSeries`: completing one occurrence must not complete the rest.
   */
  status: AppointmentStatusSchema,
  /**
   * Widen this edit to the whole rest of the series: the addressed appointment is the anchor,
   * and every LATER occurrence gets the same change. Completed and started ones are skipped.
   *
   * With `plannedStart` this is a **delta**, not an absolute: the anchor moves to the given time
   * and every later occurrence shifts by the same offset, so the recurrence pattern survives.
   *
   * No effect on an appointment that has no series.
   */
  forFullSeries: z11.boolean(),
  /**
   * Book anyway and flag every displaced entry `needsRescheduling`. Never bypasses completed
   * work — `SCHEDULE_COMPLETED_BLOCKERS` is thrown regardless.
   */
  force: z11.boolean(),
  /**
   * Same meaning as on create. Needed on update too: a `forFullSeries` time shift moves every
   * later occurrence by the same delta, so a +2-day shift can push a whole series onto Sundays.
   */
  allowOutsideWorkingHours: z11.boolean()
}).partial();
var DeleteAppointmentQuerySchema = z11.object({
  forFullSeries: z11.union([z11.boolean(), z11.enum(["true", "false"])]).optional().transform((v) => v === true || v === "true")
});
var DeleteAppointmentResultSchema = z11.object({
  deletedCount: z11.number().int(),
  deletedIds: z11.array(z11.number().int()),
  /** True when the last occurrence went and the series row was removed with it. */
  seriesRemoved: z11.boolean()
});
var AppointmentSeriesSummarySchema = z11.object({
  seriesId: z11.string().uuid(),
  frequency: RecurrenceFrequencySchema,
  /** Horizon of the series. Move it with PATCH /appointments/series/:seriesId. */
  until: z11.string().datetime(),
  totalCount: z11.number().int(),
  completedCount: z11.number().int(),
  firstPlannedStart: z11.string(),
  lastPlannedStart: z11.string(),
  appointments: z11.array(AppointmentSchema)
});
var UpdateAppointmentSeriesResultSchema = z11.object({
  createdCount: z11.number().int(),
  deletedCount: z11.number().int(),
  appointments: z11.array(AppointmentSchema)
});
var CompleteAppointmentDtoSchema = CompleteWorkItemDtoSchema.extend({
  technicianComment: z11.string().optional()
});
var FollowUpAppointmentDtoSchema = CompleteAppointmentDtoSchema.extend({
  followUpReason: z11.string().min(1)
});

// src/work-order/work-order-detail.ts
var WorkOrderWithAppointmentsSchema = WorkOrderSchema.extend({
  appointments: z12.array(AppointmentSchema)
});

// src/technician-profile/technician-profile.ts
import * as zod4 from "zod";
var z14 = zod4.z ?? zod4;
var TechnicianProfileSchema = z14.object({
  id: z14.number().int(),
  userId: z14.number().int(),
  workingDays: z14.array(z14.number().int().min(1).max(7)),
  shiftStart: z14.string().datetime(),
  shiftEnd: z14.string().datetime(),
  breakStart: z14.string().datetime(),
  trades: z14.array(z14.number().int().nonnegative())
});
var CreateTechnicianProfileDtoSchema = z14.object({
  userId: z14.number().int(),
  workingDays: z14.array(z14.number().int().min(1).max(7)),
  shiftStart: z14.string().datetime(),
  shiftEnd: z14.string().datetime(),
  breakStart: z14.string().datetime(),
  trades: z14.array(z14.number().int().nonnegative())
});

// src/gewerk/gewerk.ts
import * as zod5 from "zod";
var z16 = zod5.z ?? zod5;
var GewerkSchema = z16.object({
  id: z16.number().int(),
  text: z16.string()
});
var GewerkFiltersSchema = z16.object({
  page: z16.coerce.number().int().min(1).optional(),
  limit: z16.coerce.number().int().min(1).max(500).optional(),
  search: z16.string().optional()
});

// src/schedule/schedule.ts
import { z as z18 } from "zod";

// src/task/task.ts
import { z as z17 } from "zod";
var TaskStatusSchema = z17.enum([
  "PLANNED",
  "COMPLETED",
  "DELAYED",
  "CANCELLED"
]);
var TaskTypeSchema = z17.enum(["ERRANDS", "GENERAL_TASK"]);
var TaskTechnicianSchema = z17.object({
  id: z17.number().int(),
  fullName: z17.string()
});
var TaskListItemSchema = z17.object({
  id: z17.number().int(),
  title: z17.string(),
  taskType: TaskTypeSchema,
  status: TaskStatusSchema,
  deadline: z17.string().datetime().nullable().optional(),
  assignedTechnician: TaskTechnicianSchema.nullable(),
  schedule: ScheduleEntrySchema.nullable(),
  seriesId: z17.string().uuid().nullable(),
  /**
   * Field feedback, in the list shape as well: the disposition board is served by this row and
   * marks a block that carries a comment or files from the technician. The files themselves stay
   * on the detail shape — the board needs to know *that* there are some, not which.
   */
  technicianComment: z17.string().nullable(),
  technicianAttachmentCount: z17.number().int().min(0)
});
var TaskSchema = z17.object({
  id: z17.number().int(),
  title: z17.string(),
  description: z17.string().nullable().optional(),
  taskType: TaskTypeSchema,
  status: TaskStatusSchema,
  deadline: z17.string().datetime().nullable().optional(),
  assignedTechnicianId: z17.number().int().nullable(),
  assignedTechnician: TaskTechnicianSchema.nullable(),
  createdByUserId: z17.number().int(),
  attachments: z17.array(z17.string()),
  /** Mirrors `Appointment.technicianComment` — the one comment from the field. */
  technicianComment: z17.string().nullable(),
  /** Mirrors `Appointment.technicianAttachments`. Always present, empty when nothing was sent. */
  technicianAttachments: z17.array(TechnicianAttachmentSchema),
  schedule: ScheduleEntrySchema.nullable(),
  seriesId: z17.string().uuid().nullable(),
  createdAt: z17.string().datetime()
});
var CreateTaskDtoSchema = z17.object({
  title: z17.string().min(1),
  description: z17.string().optional(),
  assignedTechnicianId: z17.coerce.number().int().positive().nullable().optional(),
  taskType: TaskTypeSchema,
  plannedStart: z17.string().datetime().nullable().optional(),
  deadline: z17.string().datetime().optional(),
  plannedDepartureDuration: z17.number().int().min(0),
  plannedTravelDuration: z17.number().int().min(0),
  plannedWorkDuration: z17.number().int().min(0),
  attachments: z17.array(z17.string()).optional(),
  recurrence: RecurrenceInputSchema.optional(),
  /**
   * Book anyway and flag every displaced entry `needsRescheduling`. Never bypasses completed
   * work — `SCHEDULE_COMPLETED_BLOCKERS` is thrown regardless.
   *
   * With `recurrence` it applies to every occurrence: creation is all-or-nothing, so forcing
   * half a series would be meaningless.
   */
  force: z17.boolean().optional(),
  /**
   * Plan this even though the technician does not work then — a non-working day, or outside
   * their shift. Distinct from `force`: that one overrides an *overlap* and writes (it flags
   * every entry it displaced), this one overrides a read-only rule about the technician and
   * displaces nothing. A slot can need both, either, or neither.
   */
  allowOutsideWorkingHours: z17.boolean().optional()
});
var UpdateTaskDtoSchema = z17.object({
  title: z17.string().min(1).optional(),
  description: z17.string().nullable().optional(),
  assignedTechnicianId: z17.coerce.number().int().positive().optional(),
  taskType: TaskTypeSchema.optional(),
  status: TaskStatusSchema.optional(),
  plannedStart: z17.string().datetime().optional(),
  deadline: z17.string().datetime().nullable().optional(),
  plannedDepartureDuration: z17.number().int().min(0).optional(),
  actualDepartureDuration: z17.number().int().min(0).nullable().optional(),
  plannedTravelDuration: z17.number().int().min(0).optional(),
  actualTravelDuration: z17.number().int().min(0).nullable().optional(),
  plannedWorkDuration: z17.number().int().min(0).optional(),
  actualWorkDuration: z17.number().int().min(0).optional(),
  actualStart: z17.string().datetime().nullable().optional(),
  attachments: z17.array(z17.string()).optional(),
  /**
   * Widen this edit to the whole rest of the series: the addressed task is the anchor, and every
   * LATER occurrence gets the same change. Completed and started ones are skipped.
   *
   * With `plannedStart` this is a **delta**, not an absolute: the anchor moves to the given time
   * and every later occurrence shifts by the same offset, so the recurrence pattern survives.
   * `deadline` follows its occurrence, keeping the anchor's offset from the work start.
   *
   * `status` and the actuals are never spread — completing or starting one occurrence must not
   * touch the rest. No effect on a task that has no series.
   */
  forFullSeries: z17.boolean().optional(),
  /** Same meaning as on create. With `forFullSeries` it forces every later occurrence too. */
  force: z17.boolean().optional(),
  /**
   * Same meaning as on create. Needed on update too: a `forFullSeries` time shift moves every
   * later occurrence by the same delta, so a +2-day shift can push a whole series onto Sundays.
   */
  allowOutsideWorkingHours: z17.boolean().optional()
});
var DeleteTaskQuerySchema = z17.object({
  forFullSeries: z17.union([z17.boolean(), z17.enum(["true", "false"])]).optional().transform((v) => v === true || v === "true")
});
var DeleteTaskResultSchema = z17.object({
  deletedCount: z17.number().int(),
  deletedIds: z17.array(z17.number().int()),
  /** True when the last occurrence went and the series row was removed with it. */
  seriesRemoved: z17.boolean()
});
var TaskSeriesSummarySchema = z17.object({
  seriesId: z17.string().uuid(),
  frequency: RecurrenceFrequencySchema,
  /** Horizon of the series. Move it with PATCH /tasks/series/:seriesId. */
  until: z17.string().datetime(),
  totalCount: z17.number().int(),
  completedCount: z17.number().int(),
  firstPlannedStart: z17.string(),
  lastPlannedStart: z17.string(),
  tasks: z17.array(TaskSchema)
});
var UpdateTaskSeriesResultSchema = z17.object({
  createdCount: z17.number().int(),
  deletedCount: z17.number().int(),
  tasks: z17.array(TaskSchema)
});
var CompleteTaskDtoSchema = CompleteWorkItemDtoSchema.extend({
  technicianComment: z17.string().optional()
});
var ScheduleTaskDtoSchema = z17.object({
  plannedStart: z17.string().datetime(),
  plannedWorkDuration: z17.number().int().min(0),
  plannedTravelDuration: z17.number().int().min(0),
  plannedDepartureDuration: z17.number().int().min(0),
  /** See CreateTaskDtoSchema. Without it, dropping a task onto an occupied slot is a dead-end 409. */
  force: z17.boolean().optional(),
  /** See CreateTaskDtoSchema. Without it, dragging a task to a Saturday is a dead-end 409. */
  allowOutsideWorkingHours: z17.boolean().optional()
});
var TaskSortFieldSchema = z17.enum([
  "title",
  "plannedStart",
  "deadline",
  "taskType",
  "technicianName",
  "plannedWorkDuration",
  "plannedTravelDuration"
]);
var TaskSortDirectionSchema = z17.enum(["ASC", "DESC"]);
var TechnicianTasksQuerySchema = z17.object({
  from: z17.string(),
  to: z17.string()
});
var TechnicianTasksResponseSchema = z17.object({
  scheduledTasks: z17.array(TaskListItemSchema),
  unscheduledTasks: z17.array(TaskListItemSchema)
});
var TaskFiltersSchema = z17.object({
  page: z17.coerce.number().int().positive().optional(),
  limit: z17.coerce.number().int().positive().max(500).optional(),
  technicianName: z17.string().optional(),
  taskType: TaskTypeSchema.optional(),
  status: TaskStatusSchema.optional(),
  needsRescheduling: z17.coerce.boolean().optional(),
  title: z17.string().optional(),
  plannedStartFrom: z17.string().optional(),
  plannedStartTo: z17.string().optional(),
  deadlineFrom: z17.string().optional(),
  deadlineTo: z17.string().optional(),
  sortField: TaskSortFieldSchema.optional(),
  sortDirection: TaskSortDirectionSchema.optional()
});

// src/schedule/schedule.ts
var AppointmentScheduleDetailsSchema = z18.object({
  workOrderId: z18.string(),
  tradeCode: z18.number().int().min(0),
  description: z18.string().nullable().optional(),
  attachments: z18.array(z18.string()).nullable().optional(),
  serviceId: z18.number().int().nullable().optional()
});
var TaskScheduleDetailsSchema = z18.object({
  taskId: z18.number().int().positive().optional(),
  title: z18.string().min(1).optional(),
  description: z18.string().nullable().optional(),
  taskType: TaskTypeSchema.optional(),
  deadline: z18.string().datetime().nullable().optional(),
  attachments: z18.array(z18.string()).nullable().optional()
}).refine((d) => d.taskId != null || d.title != null && d.taskType != null, {
  message: "Provide taskId to schedule an existing task, or title and taskType to create one"
});
var AbsenceScheduleDetailsSchema = z18.object({
  category: AbsenceCategorySchema,
  comment: z18.string().nullable().optional()
});
var BreakScheduleDetailsSchema = z18.object({});
var SingleTechnicianSchema = z18.array(z18.coerce.number().int().positive()).length(1);
var MultiTechnicianSchema = z18.array(z18.coerce.number().int().positive()).min(1);
var scheduleEntryCommon = {
  slot: SlotSchema,
  allDay: z18.boolean().optional(),
  force: z18.boolean().optional()
};
var AllowOutsideWorkingHoursSchema = z18.boolean().optional();
var CreateAppointmentEntryDtoSchema = z18.object({
  type: z18.literal("APPOINTMENT"),
  technicianIds: SingleTechnicianSchema,
  ...scheduleEntryCommon,
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema,
  details: AppointmentScheduleDetailsSchema
});
var CreateTaskEntryDtoSchema = z18.object({
  type: z18.literal("TASK"),
  technicianIds: SingleTechnicianSchema,
  ...scheduleEntryCommon,
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema,
  details: TaskScheduleDetailsSchema
});
var CreateAbsenceEntryDtoSchema = z18.object({
  type: z18.literal("ABSENCE"),
  technicianIds: MultiTechnicianSchema,
  ...scheduleEntryCommon,
  details: AbsenceScheduleDetailsSchema
});
var CreateBreakEntryDtoSchema = z18.object({
  type: z18.literal("BREAK"),
  technicianIds: SingleTechnicianSchema,
  ...scheduleEntryCommon,
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema,
  details: BreakScheduleDetailsSchema.optional()
});
var CreateScheduleEntryDtoSchema = z18.discriminatedUnion("type", [
  CreateAppointmentEntryDtoSchema,
  CreateTaskEntryDtoSchema,
  CreateAbsenceEntryDtoSchema,
  CreateBreakEntryDtoSchema
]);
var UpdateScheduleEntryDtoSchema = z18.object({
  technicianId: z18.coerce.number().int().positive().optional(),
  slot: SlotSchema.optional(),
  allDay: z18.boolean().optional(),
  force: z18.boolean().optional(),
  /** APPOINTMENT, TASK and BREAK entries — see AllowOutsideWorkingHoursSchema. */
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema,
  actualWorkStartsAt: z18.string().datetime().nullable().optional(),
  actualEndsAt: z18.string().datetime().nullable().optional(),
  actualTravelMinutes: z18.number().int().min(0).nullable().optional(),
  actualWorkMinutes: z18.number().int().min(0).nullable().optional(),
  actualDepartureMinutes: z18.number().int().min(0).nullable().optional()
});
var UpsertScheduleBreakDtoSchema = z18.object({
  technicianId: z18.coerce.number().int().positive(),
  startsAt: z18.string().datetime(),
  allowOutsideWorkingHours: AllowOutsideWorkingHoursSchema
});

// src/schedule/schedule-read.ts
import { z as z19 } from "zod";
var ScheduleQuerySchema = z19.object({
  from: z19.string().datetime(),
  to: z19.string().datetime(),
  /**
   * Omitted means *every* technician — that is the all-workers calendar, and it is served by a
   * dedicated `@@index([starts_at, ends_at])`. A single value and a repeated one both normalise
   * to an array, because Express hands over `string` for `?technicianIds=1` and `string[]` for
   * `?technicianIds=1&technicianIds=2`.
   */
  technicianIds: z19.union([
    z19.coerce.number().int().positive(),
    z19.array(z19.coerce.number().int().positive()).max(200)
  ]).optional().transform((v) => v == null ? void 0 : Array.isArray(v) ? v : [v]),
  types: z19.union([ScheduleEntryTypeSchema, z19.array(ScheduleEntryTypeSchema)]).optional().transform((v) => v == null ? void 0 : Array.isArray(v) ? v : [v]),
  /**
   * Join each appointment's work order from Immotion (address, tenant, attachment count).
   *
   * Deliberately NOT `z.coerce.boolean()`: that coerces the *string* `"false"` to `true`, so an
   * explicit opt-out would silently do the opposite of what it says.
   */
  includeWorkOrder: z19.enum(["true", "false"]).optional().default("true").transform((v) => v === "true")
});
var ScheduleTechnicianSchema = z19.object({
  technicianId: z19.number().int(),
  technicianName: z19.string().nullable(),
  technicianAvatar: z19.string().nullable(),
  workingDays: z19.array(z19.number().int().min(1).max(7)),
  shiftStart: z19.string().datetime().nullable(),
  shiftEnd: z19.string().datetime().nullable(),
  defaultBreakStart: z19.string().datetime().nullable(),
  breakDurationMinutes: z19.number().int().min(0),
  /**
   * Everything occupying this technician's time in the window, pre-sorted by
   * `startsAt → endsAt → type → id`. Materialized rows and projected default breaks are merged
   * into this one list; a projected break is the entry with `id: null`.
   */
  entries: z19.array(ScheduleEntrySchema)
});
var ScheduleResultSchema = z19.object({
  from: z19.string().datetime(),
  to: z19.string().datetime(),
  technicians: z19.array(ScheduleTechnicianSchema),
  appointments: z19.array(AppointmentSchema),
  tasks: z19.array(TaskListItemSchema),
  absences: z19.array(AbsenceSchema)
});

// src/reschedule/reschedule.ts
import { z as z20 } from "zod";
var RescheduleItemTypeSchema = z20.enum(["APPOINTMENT", "TASK"]);
var RescheduleItemSchema = z20.object({
  id: z20.number().int(),
  type: RescheduleItemTypeSchema,
  title: z20.string(),
  scheduledDate: z20.string().datetime(),
  /** Why it is listed here. `reschedulingReason` is only filled in for cause `ABSENCE`. */
  reschedulingCause: RescheduleCauseSchema.nullable().optional(),
  reschedulingReason: z20.string().nullable().optional(),
  reschedulingRequestedAt: z20.string().nullable().optional(),
  technicianName: z20.string().nullable().optional(),
  entityMeta: z20.union([
    z20.object({ workOrderId: z20.string() }),
    z20.object({ taskType: z20.string() }),
    z20.object({ category: z20.string() })
  ])
});
var RescheduleFiltersSchema = z20.object({
  page: z20.coerce.number().int().positive().optional(),
  limit: z20.coerce.number().int().positive().max(500).optional(),
  type: RescheduleItemTypeSchema.optional(),
  reason: AbsenceCategorySchema.optional(),
  technicianName: z20.string().optional(),
  dateFrom: z20.string().optional(),
  dateTo: z20.string().optional(),
  title: z20.string().optional(),
  sortField: z20.enum(["scheduledDate", "type", "technicianName"]).optional(),
  sortDirection: z20.enum(["ASC", "DESC"]).optional()
});
var RescheduleCountResponseSchema = z20.object({
  count: z20.number().int()
});

// src/controlling/controlling.ts
import * as zod6 from "zod";
var z22 = zod6.z ?? zod6;
var ControllingFollowUpReasonCodeSchema = z22.enum([
  "MIETER_NICHT_VOR_ORT",
  "TERMIN_NICHT_EINGEHALTEN",
  "WEITERER_GRUND",
  "MIT_ANDEREM_GEWERK",
  "MATERIAL_BESTELLUNG"
]);
var ControllingReasonFilterValueSchema = z22.union([
  ControllingFollowUpReasonCodeSchema,
  AbsenceCategorySchema
]);
var ControllingSortFieldSchema = z22.enum([
  "repairDate",
  "workOrderId",
  "technician",
  "tenant",
  "ve",
  "building",
  "trade",
  "gewerkId",
  "actualWorkHours",
  "plannedWorkHours",
  "actualTravelHours",
  "plannedTravelHours",
  "actualTotalHours",
  "deviation",
  "deviationHours",
  "wertEuro"
]);
var ScheduleStatusSchema = z22.enum([
  "WITHOUT_FOLLOW_UP",
  "WITH_FOLLOW_UPS",
  "RESCHEDULED_BY_OFFICE",
  "RESCHEDULED_BY_TECHNICIAN"
]);
var DeviationStatusSchema = z22.enum([
  "ON_SCHEDULE",
  "OVER_TARGET",
  "UNDER_TARGET"
]);
var ControllingFiltersSchema = z22.object({
  page: z22.coerce.number().int().positive().optional(),
  limit: z22.coerce.number().int().positive().max(100).optional(),
  sortField: ControllingSortFieldSchema.optional(),
  sortDirection: z22.enum(["ASC", "DESC"]).optional(),
  dateFrom: z22.string().optional(),
  dateTo: z22.string().optional(),
  workOrderId: z22.string().optional(),
  technicianId: z22.coerce.number().int().optional(),
  tenant: z22.string().optional(),
  ve: z22.string().optional(),
  building: z22.string().optional(),
  tradeId: z22.coerce.number().int().optional(),
  technicianTradeId: z22.coerce.number().int().optional(),
  scheduleStatus: z22.array(ScheduleStatusSchema).optional(),
  deviationStatus: z22.array(DeviationStatusSchema).optional(),
  actualTravelHoursMin: z22.coerce.number().optional(),
  actualTravelHoursMax: z22.coerce.number().optional(),
  actualWorkHoursMin: z22.coerce.number().optional(),
  actualWorkHoursMax: z22.coerce.number().optional(),
  actualTotalHoursMin: z22.coerce.number().optional(),
  actualTotalHoursMax: z22.coerce.number().optional(),
  deviationMin: z22.coerce.number().optional(),
  deviationMax: z22.coerce.number().optional(),
  reasons: z22.array(ControllingReasonFilterValueSchema).optional()
});
var ControllingAggregatesSchema = z22.object({
  totalPlannedWorkHours: z22.number(),
  totalActualWorkHours: z22.number(),
  totalPlannedTravelHours: z22.number(),
  totalActualTravelHours: z22.number(),
  totalPlannedHours: z22.number(),
  totalActualHours: z22.number(),
  deviation: z22.number(),
  totalWertEuro: z22.number()
});
var ControllingRowSchema = z22.object({
  workOrderId: z22.string(),
  tenant: z22.string(),
  technicians: z22.array(z22.string()),
  ve: z22.string(),
  building: z22.string(),
  trade: z22.string(),
  scheduleStatus: ScheduleStatusSchema,
  deviationStatus: DeviationStatusSchema,
  actualWorkHours: z22.number(),
  plannedWorkHours: z22.number(),
  actualTravelHours: z22.number(),
  plannedTravelHours: z22.number(),
  actualTotalHours: z22.number(),
  plannedTotalHours: z22.number(),
  repairDateFrom: z22.string(),
  repairDateTo: z22.string(),
  appointmentCount: z22.number(),
  deviation: z22.number(),
  reasons: z22.array(z22.string()),
  technicianTradeIds: z22.array(z22.number().int()),
  gewerkId: z22.number().int().nullable(),
  deviationHours: z22.number(),
  wertEuro: z22.number().nullable(),
  preis: z22.number().nullable()
});
var ControllingPdfColumnSchema = z22.enum([
  "workOrderId",
  "scheduleStatus",
  "repairDate",
  "building",
  "tenant",
  "gewerkId",
  "trade",
  "ve",
  "technician",
  "plannedWorkHours",
  "actualWorkHours",
  "plannedTravelHours",
  "actualTravelHours",
  "plannedTotalHours",
  "actualTotalHours",
  "deviation",
  "deviationHours",
  "reasons",
  "wertEuro"
]);
var ControllingActiveFilterSchema = z22.object({
  label: z22.string(),
  value: z22.string()
});
var ControllingPdfRequestBodySchema = z22.object({
  dateFrom: z22.string().optional(),
  dateTo: z22.string().optional(),
  workOrderId: z22.string().optional(),
  technicianId: z22.coerce.number().int().optional(),
  tenant: z22.string().optional(),
  ve: z22.string().optional(),
  building: z22.string().optional(),
  tradeId: z22.coerce.number().int().optional(),
  technicianTradeId: z22.coerce.number().int().optional(),
  scheduleStatus: z22.array(ScheduleStatusSchema).optional(),
  deviationStatus: z22.array(DeviationStatusSchema).optional(),
  actualTravelHoursMin: z22.coerce.number().optional(),
  actualTravelHoursMax: z22.coerce.number().optional(),
  actualWorkHoursMin: z22.coerce.number().optional(),
  actualWorkHoursMax: z22.coerce.number().optional(),
  actualTotalHoursMin: z22.coerce.number().optional(),
  actualTotalHoursMax: z22.coerce.number().optional(),
  deviationMin: z22.coerce.number().optional(),
  deviationMax: z22.coerce.number().optional(),
  reasons: z22.array(ControllingReasonFilterValueSchema).optional(),
  sortField: ControllingSortFieldSchema.optional(),
  sortDirection: z22.enum(["ASC", "DESC"]).optional(),
  selectedColumns: z22.array(ControllingPdfColumnSchema).min(1),
  activeFilters: z22.array(ControllingActiveFilterSchema)
});
var ControllingXlsxRequestBodySchema = ControllingPdfRequestBodySchema.omit({
  activeFilters: true
});

// src/mail-setting/mail-setting.ts
import * as zod7 from "zod";
var z24 = zod7.z ?? zod7;
var MailSettingSchema = z24.object({
  host: z24.string().min(1),
  port: z24.coerce.number().int().min(1).max(65535),
  user: z24.string(),
  from: z24.string().email()
});
var MailSettingResponseSchema = MailSettingSchema.extend({
  isPassSet: z24.boolean(),
  werkhofleiterEmails: z24.array(z24.string().email()).default([])
});
var UpdateMailSettingDtoSchema = z24.object({
  host: z24.string().min(1).optional(),
  port: z24.coerce.number().int().min(1).max(65535).optional(),
  user: z24.string().optional(),
  pass: z24.string().trim().min(1).optional(),
  from: z24.string().email().optional(),
  werkhofleiterEmails: z24.array(z24.string().email()).optional()
});

// src/arbeitszeit-report/arbeitszeit-report.ts
import * as zod8 from "zod";
var z26 = zod8.z ?? zod8;
var ArbeitszeitGesamtCategorySchema = z26.enum([
  "auftraege",
  "allgemeinzeit",
  "urlaub",
  "krankheit",
  "privat",
  "gesamt"
]);
var ArbeitszeitGesamtRowSchema = z26.object({
  category: ArbeitszeitGesamtCategorySchema,
  sollStd: z26.number().nullable(),
  istStd: z26.number(),
  abweichungStd: z26.number().nullable(),
  abweichungPct: z26.number().nullable(),
  wertEuro: z26.number().nullable()
});
var AuslastungRowSchema = z26.object({
  technicianId: z26.number().int(),
  technicianName: z26.string(),
  sollStd: z26.number(),
  reparaturzeitenStd: z26.number(),
  nichtReparaturzeitenStd: z26.number(),
  istStd: z26.number(),
  abwesenheitenStd: z26.number(),
  auslastungPct: z26.number().nullable()
});
var ArbeitsorgOrganisationTypeSchema = z26.enum([
  "ERRANDS",
  "GENERAL_TASK",
  "SICK_LEAVE",
  "VACATION",
  "PERSONAL_LEAVE",
  "TRAINING"
]);
var ArbeitsorgRowSchema = z26.object({
  id: z26.string(),
  technicianId: z26.number().int().nullable(),
  technicianName: z26.string(),
  organisationType: ArbeitsorgOrganisationTypeSchema,
  bereich: z26.string().nullable(),
  dateFrom: z26.string(),
  dateTo: z26.string(),
  timeFrom: z26.string().nullable(),
  timeTo: z26.string().nullable(),
  istStd: z26.number().nullable(),
  wertEuro: z26.number().nullable()
});
var PaginatedMetaSchema = z26.object({
  page: z26.number().int(),
  limit: z26.number().int(),
  total: z26.number().int()
});
var ArbeitszeitReportFiltersSchema = z26.object({
  dateFrom: z26.string().optional(),
  dateTo: z26.string().optional(),
  technicianId: z26.coerce.number().int().optional(),
  tradeId: z26.coerce.number().int().optional(),
  scheduleStatus: z26.array(ScheduleStatusSchema).optional(),
  deviationStatus: z26.array(DeviationStatusSchema).optional(),
  pageAuftraege: z26.coerce.number().int().positive().optional(),
  pageAuslastung: z26.coerce.number().int().positive().optional(),
  pageArbeitsorganisation: z26.coerce.number().int().positive().optional()
});
var ArbeitszeitReportResponseSchema = z26.object({
  gesamtuebersicht: z26.array(ArbeitszeitGesamtRowSchema),
  auslastung: z26.object({
    items: z26.array(AuslastungRowSchema),
    meta: PaginatedMetaSchema
  }),
  auftraege: z26.object({
    items: z26.array(ControllingRowSchema),
    meta: PaginatedMetaSchema,
    aggregates: ControllingAggregatesSchema
  }),
  arbeitsorganisation: z26.object({
    items: z26.array(ArbeitsorgRowSchema),
    meta: PaginatedMetaSchema
  })
});

// src/push-subscription/push-subscription.ts
import { z as z27 } from "zod";
var PushSubscriptionKeysSchema = z27.object({
  /** Client public key. Payloads are encrypted to it (RFC 8291), so the push service relays ciphertext. */
  p256dh: z27.string().min(1, "p256dh fehlt"),
  /** 16-byte shared secret that feeds the same encryption. */
  auth: z27.string().min(1, "auth fehlt")
});
var PushEndpointSchema = z27.string().regex(/^https:\/\/\S+$/, "Ung\xFCltiger Push-Endpunkt").max(2048);
var SubscribePushDtoSchema = z27.object({
  endpoint: PushEndpointSchema,
  keys: PushSubscriptionKeysSchema,
  userAgent: z27.string().max(512).optional()
});
var UnsubscribePushDtoSchema = z27.object({
  endpoint: PushEndpointSchema
});
var PushDeviceSchema = z27.object({
  id: z27.number().int(),
  userAgent: z27.string().nullable(),
  createdAt: z27.string().datetime(),
  lastSeenAt: z27.string().datetime()
});
var VapidPublicKeyResponseSchema = z27.object({
  publicKey: z27.string().min(1)
});
var SendTestPushResponseSchema = z27.object({
  sent: z27.number().int().nonnegative(),
  failed: z27.number().int().nonnegative(),
  removed: z27.number().int().nonnegative()
});

// src/push-message/push-message.ts
import { z as z28 } from "zod";
var PUSH_TARGET_PATH_PATTERN = /^\/(?![/\\])[^\s\\]*$/;
var PUSH_TARGET_PATH_MESSAGE = "Pfad muss mit einem einzelnen / beginnen";
var SendPushMessageDtoSchema = z28.object({
  userIds: z28.array(z28.number().int().positive()).min(1, "Empf\xE4nger fehlen").max(500),
  title: z28.string().min(1, "Titel fehlt").max(80),
  body: z28.string().min(1, "Text fehlt").max(400),
  /** App-relative on purpose — see PUSH_TARGET_PATH_PATTERN for what that costs to enforce. */
  url: z28.string().regex(PUSH_TARGET_PATH_PATTERN, PUSH_TARGET_PATH_MESSAGE).max(512).optional()
});
var PushStatusFilterSchema = z28.enum(["with", "without", "all"]);
var PushAdminSortFieldSchema = z28.enum(["name", "devices"]);
var PushAdminUsersFiltersSchema = z28.preprocess(
  (raw) => {
    if (raw !== null && typeof raw === "object" && !Array.isArray(raw)) {
      const { role, roles, ...rest } = raw;
      const normalizedRoles = roles !== void 0 ? Array.isArray(roles) ? roles : [roles] : role !== void 0 ? [role] : void 0;
      return normalizedRoles !== void 0 ? { ...rest, roles: normalizedRoles } : rest;
    }
    return raw;
  },
  z28.object({
    page: z28.coerce.number().int().positive().optional(),
    limit: z28.coerce.number().int().positive().max(500).optional(),
    search: z28.string().optional(),
    roles: z28.array(UserRoleSchema).optional(),
    pushStatus: PushStatusFilterSchema.optional(),
    sortField: PushAdminSortFieldSchema.optional(),
    sortOrder: z28.enum(["ASC", "DESC"]).optional()
  })
);
var PushAdminUserSchema = z28.object({
  id: z28.number().int(),
  name: z28.string(),
  email: z28.string(),
  roles: z28.array(UserRoleSchema),
  devices: z28.array(PushDeviceSchema)
});
var PaginatedMetaSchema2 = z28.object({
  page: z28.number().int(),
  limit: z28.number().int(),
  total: z28.number().int()
});
var PushAdminUsersResponseSchema = z28.object({
  items: z28.array(PushAdminUserSchema),
  meta: PaginatedMetaSchema2
});
var PushAdminStatsSchema = z28.object({
  usersTotal: z28.number().int().nonnegative(),
  usersWithPush: z28.number().int().nonnegative(),
  devicesTotal: z28.number().int().nonnegative()
});
var PushMessageSchema = z28.object({
  id: z28.number().int(),
  title: z28.string(),
  body: z28.string(),
  url: z28.string().nullable(),
  createdAt: z28.string().datetime(),
  /** Null when the author's account was deleted since. */
  createdBy: z28.object({ id: z28.number().int(), name: z28.string() }).nullable(),
  /** Who was addressed, by name — the Verlauf's answer to "an wen ging das?". */
  recipients: z28.array(z28.object({ id: z28.number().int(), name: z28.string() })),
  recipientCount: z28.number().int().nonnegative(),
  sentCount: z28.number().int().nonnegative(),
  failedCount: z28.number().int().nonnegative(),
  removedCount: z28.number().int().nonnegative()
});
var PushMessageListFiltersSchema = z28.object({
  page: z28.coerce.number().int().positive().optional(),
  limit: z28.coerce.number().int().positive().max(500).optional()
});
var PushMessageListResponseSchema = z28.object({
  items: z28.array(PushMessageSchema),
  meta: PaginatedMetaSchema2
});
var SendPushMessageResponseSchema = PushMessageSchema.extend({
  /** Requested ids that were archived or unknown — shown to the admin, never swallowed. */
  skippedUserIds: z28.array(z28.number().int())
});
export {
  AbsenceCategorySchema,
  AbsenceFiltersSchema,
  AbsenceListResultSchema,
  AbsenceScheduleDetailsSchema,
  AbsenceSchema,
  AbsenceSortDirectionSchema,
  AbsenceSortFieldSchema,
  AbsenceTechnicianSchema,
  AllowOutsideWorkingHoursSchema,
  AppointmentScheduleDetailsSchema,
  AppointmentSchema,
  AppointmentSeriesSummarySchema,
  AppointmentStatusSchema,
  AppointmentTechnicianSchema,
  ArbeitsorgOrganisationTypeSchema,
  ArbeitsorgRowSchema,
  ArbeitszeitGesamtCategorySchema,
  ArbeitszeitGesamtRowSchema,
  ArbeitszeitReportFiltersSchema,
  ArbeitszeitReportResponseSchema,
  AuditLogActionSchema,
  AuditLogActorSchema,
  AuditLogEntityTypeSchema,
  AuditLogFiltersSchema,
  AuditLogSchema,
  AuslastungRowSchema,
  AuthErrorCode,
  BreakScheduleDetailsSchema,
  CompleteAppointmentDtoSchema,
  CompleteTaskDtoSchema,
  CompleteWorkItemDtoSchema,
  ControllingActiveFilterSchema,
  ControllingAggregatesSchema,
  ControllingFiltersSchema,
  ControllingFollowUpReasonCodeSchema,
  ControllingPdfColumnSchema,
  ControllingPdfRequestBodySchema,
  ControllingReasonFilterValueSchema,
  ControllingRowSchema,
  ControllingSortFieldSchema,
  ControllingXlsxRequestBodySchema,
  CreateAbsenceDtoSchema,
  CreateAbsenceEntryDtoSchema,
  CreateAppointmentDtoSchema,
  CreateAppointmentEntryDtoSchema,
  CreateBreakEntryDtoSchema,
  CreateScheduleEntryDtoSchema,
  CreateTaskDtoSchema,
  CreateTaskEntryDtoSchema,
  CreateTechnicianProfileDtoSchema,
  CreateUserDtoSchema,
  CreateWorkOrderDtoSchema,
  CurrentWorkSessionResponseSchema,
  CurrentWorkSessionSchema,
  DeleteAppointmentQuerySchema,
  DeleteAppointmentResultSchema,
  DeleteTaskQuerySchema,
  DeleteTaskResultSchema,
  DeviationStatusSchema,
  EndDepartureDtoSchema,
  FollowUpAppointmentDtoSchema,
  GewerkFiltersSchema,
  GewerkSchema,
  ImmotionAttachmentSchema,
  MailSettingResponseSchema,
  MailSettingSchema,
  PUSH_TARGET_PATH_MESSAGE,
  PUSH_TARGET_PATH_PATTERN,
  PaginatedMetaSchema,
  PushAdminSortFieldSchema,
  PushAdminStatsSchema,
  PushAdminUserSchema,
  PushAdminUsersFiltersSchema,
  PushAdminUsersResponseSchema,
  PushDeviceSchema,
  PushMessageListFiltersSchema,
  PushMessageListResponseSchema,
  PushMessageSchema,
  PushStatusFilterSchema,
  PushSubscriptionKeysSchema,
  ROLE_RANK,
  RecurrenceFrequencySchema,
  RecurrenceInputSchema,
  RescheduleCauseSchema,
  RescheduleCountResponseSchema,
  RescheduleFiltersSchema,
  RescheduleItemSchema,
  RescheduleItemTypeSchema,
  Role,
  ScheduleConflictItemSchema,
  ScheduleEntrySchema,
  ScheduleEntryTechnicianSchema,
  ScheduleEntryTypeSchema,
  ScheduleQuerySchema,
  ScheduleResultSchema,
  ScheduleSeriesSchema,
  ScheduleSeriesTypeSchema,
  ScheduleStatusSchema,
  ScheduleTaskDtoSchema,
  ScheduleTechnicianSchema,
  ScheduleWorkingHoursViolationReasonSchema,
  ScheduleWorkingHoursViolationSchema,
  SegmentedSlotSchema,
  SendPushMessageDtoSchema,
  SendPushMessageResponseSchema,
  SendTestPushResponseSchema,
  SlotSchema,
  SpanSlotSchema,
  SubscribePushDtoSchema,
  TaskFiltersSchema,
  TaskListItemSchema,
  TaskScheduleDetailsSchema,
  TaskSchema,
  TaskSeriesSummarySchema,
  TaskSortDirectionSchema,
  TaskSortFieldSchema,
  TaskStatusSchema,
  TaskTechnicianSchema,
  TaskTypeSchema,
  TechnicianAttachmentSchema,
  TechnicianProfileDataSchema,
  TechnicianProfileSchema,
  TechnicianTasksQuerySchema,
  TechnicianTasksResponseSchema,
  UnsubscribePushDtoSchema,
  UpdateAbsenceDtoSchema,
  UpdateAppointmentDtoSchema,
  UpdateAppointmentSeriesResultSchema,
  UpdateMailSettingDtoSchema,
  UpdateScheduleEntryDtoSchema,
  UpdateScheduleSeriesDtoSchema,
  UpdateTaskDtoSchema,
  UpdateTaskSeriesResultSchema,
  UpdateTechnicianCommentDtoSchema,
  UpdateUserDtoSchema,
  UpsertScheduleBreakDtoSchema,
  UserFiltersSchema,
  UserRoleSchema,
  UserRolesSchema,
  UserStatusFilterSchema,
  VapidPublicKeyResponseSchema,
  WorkOrderExternalSnapshotSchema,
  WorkOrderFiltersSchema,
  WorkOrderLocalDataSchema,
  WorkOrderMieterSchema,
  WorkOrderSchema,
  WorkOrderSortDirectionSchema,
  WorkOrderSortFieldSchema,
  WorkOrderStatusSchema,
  WorkOrderWithAppointmentsSchema,
  WorkPhaseSchema,
  WorkSessionItemTypeSchema,
  WorkTimeFieldSchema,
  deriveWorkPhase,
  hasAnyRole,
  hasOnlyRole,
  hasRole,
  highestRole,
  isSessionOpen
};
